import React from 'react';
import { DeadlineRule } from '../types';
import CustomDropdown from '../../../UI/CustomDropdown';
import sidePanelStyles from '../../../../styles/SidePanel.module.css';
import customDropdownStyles from '../../../../styles/CustomDropdown.module.css';
import buttonStyles from '../../../../styles/Buttons.module.css';
import styles from '../../../../styles/PointConfigurationTab.module.css';

interface DeadlineRuleEditorProps {
  rule: DeadlineRule;
  onUpdate: (ruleId: string, field: string, value: any) => void;
  onRemove: (ruleId: string) => void;
}

const DeadlineRuleEditor: React.FC<DeadlineRuleEditorProps> = ({ 
  rule, 
  onUpdate, 
  onRemove 
}) => {
  const renderTimeInputs = (prefix: 'Before' | 'After' | 'From' | 'To') => {
    const isBefore = prefix === 'Before';
    const isAfter = prefix === 'After';
    const isFrom = prefix === 'From';
    const isTo = prefix === 'To';
    
    const days = isBefore ? rule.daysBefore : isAfter ? rule.daysAfter : isFrom ? rule.rangeFromDays : rule.rangeToDays || 0;
    const hours = isBefore ? rule.hoursBefore : isAfter ? rule.hoursAfter : isFrom ? rule.rangeFromHours : rule.rangeToHours || 0;
    const minutes = isBefore ? rule.minutesBefore : isAfter ? rule.minutesAfter : isFrom ? rule.rangeFromMinutes : rule.rangeToMinutes || 0;
    const seconds = isBefore ? rule.secondsBefore : isAfter ? rule.secondsAfter : isFrom ? rule.rangeFromSeconds : rule.rangeToSeconds || 0;
    
    const updateDays = (value: number) => {
      if (isBefore) onUpdate(rule.id, 'daysBefore', value);
      else if (isAfter) onUpdate(rule.id, 'daysAfter', value);
      else if (isFrom) onUpdate(rule.id, 'rangeFromDays', value);
      else onUpdate(rule.id, 'rangeToDays', value);
    };
    
    const updateHours = (value: number) => {
      if (isBefore) onUpdate(rule.id, 'hoursBefore', value);
      else if (isAfter) onUpdate(rule.id, 'hoursAfter', value);
      else if (isFrom) onUpdate(rule.id, 'rangeFromHours', value);
      else onUpdate(rule.id, 'rangeToHours', value);
    };
    
    const updateMinutes = (value: number) => {
      if (isBefore) onUpdate(rule.id, 'minutesBefore', value);
      else if (isAfter) onUpdate(rule.id, 'minutesAfter', value);
      else if (isFrom) onUpdate(rule.id, 'rangeFromMinutes', value);
      else onUpdate(rule.id, 'rangeToMinutes', value);
    };
    
    const updateSeconds = (value: number) => {
      if (isBefore) onUpdate(rule.id, 'secondsBefore', value);
      else if (isAfter) onUpdate(rule.id, 'secondsAfter', value);
      else if (isFrom) onUpdate(rule.id, 'rangeFromSeconds', value);
      else onUpdate(rule.id, 'rangeToSeconds', value);
    };
    
    return (
      <div className={styles.timeInputGroup}>
        <div className={styles.timeInputRow}>
          <div className={styles.timeInputField}>
            <label className={styles.timeInputLabel}>Days</label>
            <input
              type="number"
              className={sidePanelStyles.formControl}
              value={days || 0}
              min="0"
              max="365"
              onChange={(e) => updateDays(parseInt(e.target.value) || 0)}
              placeholder="0"
            />
          </div>
          <div className={styles.timeInputField}>
            <label className={styles.timeInputLabel}>Hours</label>
            <input
              type="number"
              className={sidePanelStyles.formControl}
              value={hours || 0}
              min="0"
              max="23"
              onChange={(e) => updateHours(parseInt(e.target.value) || 0)}
              placeholder="0"
            />
          </div>
          <div className={styles.timeInputField}>
            <label className={styles.timeInputLabel}>Minutes</label>
            <input
              type="number"
              className={sidePanelStyles.formControl}
              value={minutes || 0}
              min="0"
              max="59"
              onChange={(e) => updateMinutes(parseInt(e.target.value) || 0)}
              placeholder="0"
            />
          </div>
          <div className={styles.timeInputField}>
            <label className={styles.timeInputLabel}>Seconds</label>
            <input
              type="number"
              className={sidePanelStyles.formControl}
              value={seconds || 0}
              min="0"
              max="59"
              onChange={(e) => updateSeconds(parseInt(e.target.value) || 0)}
              placeholder="0"
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.deadlineRuleItem}>
      <div className={styles.ruleHeader}>
        <div>
          <div className={styles.ruleTitle}>{rule.ruleName || 'New Deadline Rule'}</div>
          <div className={styles.ruleDescription}>Configure points for specific time period</div>
        </div>
        <button 
          type="button" 
          className={`${buttonStyles.btn} ${buttonStyles.btnSm} ${buttonStyles.btnDanger}`}
          onClick={() => onRemove(rule.id)}
        >
          <i className="bi bi-trash"></i>
        </button>
      </div>
      <div className={styles.ruleContent}>
        <div className={sidePanelStyles.formGroup}>
          <label className={sidePanelStyles.formLabel}>Rule Name</label>
          <input 
            type="text" 
            className={sidePanelStyles.formControl} 
            placeholder="Early Bird Bonus"
            value={rule.ruleName}
            onChange={(e) => onUpdate(rule.id, 'ruleName', e.target.value)}
          />
        </div>

        <div className={sidePanelStyles.formGroup}>
          <label className={sidePanelStyles.formLabel}>Time Period</label>
          <CustomDropdown
            options={[
              { value: 'before', label: 'Before Deadline' },
              { value: 'after', label: 'After Deadline' },
              { value: 'range', label: 'Time Range Before Deadline' }
            ]}
            value={rule.timePeriodType}
            onChange={(value) => onUpdate(rule.id, 'timePeriodType', Array.isArray(value) ? value[0] : value)}
            placeholder="Select time period"
            className={customDropdownStyles.sizeFull}
          />
        </div>

        {/* Before Deadline Time Configuration */}
        {rule.timePeriodType === 'before' && (
          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel}>Time Before Deadline</label>
            <div className={sidePanelStyles.formHelp} style={{ marginBottom: '0.75rem' }}>
              Enter the duration before the task deadline (e.g., 5 days, 2 hours before deadline)
            </div>
            {renderTimeInputs('Before')}
          </div>
        )}

        {/* After Deadline Time Configuration */}
        {rule.timePeriodType === 'after' && (
          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel}>Time After Deadline</label>
            <div className={sidePanelStyles.formHelp} style={{ marginBottom: '0.75rem' }}>
              Enter the duration after the task deadline (e.g., 1 day, 3 hours after deadline)
            </div>
            {renderTimeInputs('After')}
          </div>
        )}

        {/* Range Time Configuration */}
        {rule.timePeriodType === 'range' && (
          <div className={sidePanelStyles.formGroup}>
            <label className={sidePanelStyles.formLabel}>Time Range Before Deadline</label>
            <div className={sidePanelStyles.formHelp} style={{ marginBottom: '0.75rem' }}>
              Define a time range before the deadline (e.g., from 7 days to 2 days before deadline)
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
              <div>
                <label className={sidePanelStyles.formLabel} style={{ marginBottom: '0.5rem', display: 'block' }}>
                  From (Earlier)
                </label>
                {renderTimeInputs('From')}
              </div>
              <div>
                <label className={sidePanelStyles.formLabel} style={{ marginBottom: '0.5rem', display: 'block' }}>
                  To (Later)
                </label>
                {renderTimeInputs('To')}
              </div>
            </div>
          </div>
        )}

        <div className={sidePanelStyles.formGroup}>
          <label className={sidePanelStyles.formLabel}>Point Calculation</label>
          <CustomDropdown
            options={[
              { value: 'fixed', label: 'Fixed Points' },
              { value: 'percentage', label: 'Percentage of Base Points' },
              { value: 'multiplier', label: 'Multiplier' }
            ]}
            value={rule.calculationType}
            onChange={(value) => onUpdate(rule.id, 'calculationType', Array.isArray(value) ? value[0] : value)}
            placeholder="Select calculation type"
            className={customDropdownStyles.sizeFull}
          />
        </div>

        <div className={sidePanelStyles.formGroup}>
          <label className={sidePanelStyles.formLabel}>Value</label>
          <input 
            type="number" 
            className={sidePanelStyles.formControl} 
            value={rule.pointValue} 
            min="-10000" 
            max="10000" 
            step="0.1"
            onChange={(e) => onUpdate(rule.id, 'pointValue', parseFloat(e.target.value))}
          />
          <div className={sidePanelStyles.formHelp}>
            For percentage: enter percentage (e.g., 5 for 5%). For fixed: enter points. For multiplier: enter factor (e.g., 1.5 for 1.5x)
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeadlineRuleEditor;

